/**
 * Libro IVA Digital — archivos TXT de ancho fijo (ventas).
 *
 * Diseño oficial ARCA (libro-iva-digital-diseno-registros.pdf):
 *  - VENTAS_CBTE      → 266 posiciones por comprobante.
 *  - VENTAS_ALICUOTAS → 62 posiciones por alícuota del comprobante.
 *
 * Importes: 15 dígitos = 13 enteros + 2 decimales, sin punto, cero a la izq.
 * El archivo de "Comprobantes Anulados" (44 pos) no se genera: las
 * anulaciones de Vuelto se emiten como Nota de Crédito (camino AFIP correcto),
 * que ya entra en VENTAS_CBTE/ALICUOTAS.
 */

/** Código de alícuota en el TXT de alícuotas (tabla oficial). Exportado para el builder de compras. */
export const ALICUOTA_CODE: Record<number, string> = {
  3: '0003', // 0%
  4: '0004', // 10,5%
  5: '0005', // 21%
  6: '0006', // 27%
  8: '0008', // 5%
  9: '0009', // 2,5%
};

export type LibroIvaBreakdown = {
  alicuotas?: Array<{ id: number; baseImp: number; importe: number }> | null;
  impNeto?: number | null;
  impIva?: number | null;
  impOpEx?: number | null;
  impTotConc?: number | null;
};

/** Fila fuente: un comprobante fiscal (factura, NC o ND) aprobado. */
export type LibroIvaVentaRow = {
  issuedAt: string; // ISO
  invoiceType: number; // CbteTipo AFIP
  pointOfSale: number;
  invoiceNumber: number;
  buyerCuit: string | null;
  buyerName: string | null;
  totalAmount: number;
  netAmount: number;
  ivaAmount: number;
  ivaBreakdown?: LibroIvaBreakdown | null;
};

export type LibroIvaAlicuota = {
  /** Código de 4 dígitos: '0003'..'0009'. */
  code: string;
  baseImp: number;
  importe: number;
};

const digits = (v: string | null | undefined): string => String(v ?? '').replace(/\D/g, '');

const padNum = (value: number, intLen: number, decLen = 2): string => {
  const cents = Math.round(Math.abs(value) * 100);
  const str = String(cents).padStart(intLen + decLen, '0');
  return str.slice(-(intLen + decLen));
};

const padZeros = (value: number | string, len: number): string => {
  const d = String(value).replace(/\D/g, '');
  return (d || '0').padStart(len, '0').slice(-len);
};

const normalizeName = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Z0-9 .,'&()-]/gi, '')
    .toUpperCase();

const padAlpha = (value: string, len: number): string => {
  const clean = normalizeName(value).slice(0, len);
  return clean.padEnd(len, ' ');
};

const afipDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '00000000';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
};

const buyerDocType = (cuit: string | null): string => {
  const d = digits(cuit);
  if (d.length === 11) return '80'; // CUIT
  if (d.length >= 7 && d.length <= 8) return '96'; // DNI
  return '99'; // Consumidor Final / sin identificar
};

const buyerDocNumber = (cuit: string | null): string => {
  const d = digits(cuit);
  return d ? d.padStart(20, '0').slice(-20) : '0'.repeat(20);
};

/**
 * Alícuotas a exportar de un comprobante.
 * 1) Desglose persistido (iva_breakdown, ids AFIP → códigos TXT).
 * 2) Legacy (sin desglose): una fila 21% desde neto/iva agregados;
 *    si neto=iva=0 → fila 0% con baseImp = total (monotributo FC).
 */
export function resolveAlicuotas(row: LibroIvaVentaRow): LibroIvaAlicuota[] {
  const persisted = row.ivaBreakdown?.alicuotas?.filter((a) => ALICUOTA_CODE[a.id]);
  if (persisted?.length) {
    return persisted.map((a) => ({
      code: ALICUOTA_CODE[a.id],
      baseImp: a.baseImp,
      importe: a.importe,
    }));
  }
  if (Math.abs(row.netAmount) > 0.004 || Math.abs(row.ivaAmount) > 0.004) {
    return [{ code: '0005', baseImp: row.netAmount, importe: row.ivaAmount }];
  }
  return [{ code: '0003', baseImp: row.totalAmount, importe: 0 }];
}

const operationCode = (row: LibroIvaVentaRow): string => {
  const bd = row.ivaBreakdown;
  const gravado =
    Math.abs(bd?.impNeto ?? row.netAmount) + Math.abs(bd?.impIva ?? row.ivaAmount);
  if (gravado <= 0.004 && Math.abs(bd?.impOpEx ?? 0) > 0.004) return 'E';
  if (gravado <= 0.004 && Math.abs(bd?.impTotConc ?? 0) > 0.004) return 'N';
  return '0';
};

/** VENTAS_CBTE — 266 posiciones por comprobante. */
export function buildVentasCbteTxt(rows: LibroIvaVentaRow[]): string {
  const sorted = [...rows].sort(
    (a, b) =>
      a.issuedAt.localeCompare(b.issuedAt) ||
      a.invoiceType - b.invoiceType ||
      a.pointOfSale - b.pointOfSale ||
      a.invoiceNumber - b.invoiceNumber
  );
  return sorted
    .map((r) => {
      const bd = r.ivaBreakdown;
      const impTotConc = bd?.impTotConc ?? 0;
      const impOpEx = bd?.impOpEx ?? 0;
      const alicuotas = resolveAlicuotas(r);
      const cantAlicuotas = Math.min(9, Math.max(1, alicuotas.length));
      const fecha = afipDate(r.issuedAt);
      return (
        fecha + // 1-8 fecha cbte
        padZeros(r.invoiceType, 3) + // 9-11 tipo cbte
        padZeros(r.pointOfSale, 5) + // 12-16 punto de venta
        padZeros(r.invoiceNumber, 20) + // 17-36 nro desde
        padZeros(r.invoiceNumber, 20) + // 37-56 nro hasta
        buyerDocType(r.buyerCuit) + // 57-58 doc tipo
        buyerDocNumber(r.buyerCuit) + // 59-78 doc nro
        padAlpha(r.buyerName ?? 'CONSUMIDOR FINAL', 30) + // 79-108 apellido/nombre
        padNum(r.totalAmount, 13) + // 109-123 imp total
        padNum(impTotConc, 13) + // 124-138 no gravado
        padNum(0, 13) + // 139-153 percep. no categorizados
        padNum(impOpEx, 13) + // 154-168 operaciones exentas
        padNum(0, 13) + // 169-183 percep. imp. nacionales
        padNum(0, 13) + // 184-198 percep. IIBB
        padNum(0, 13) + // 199-213 percep. municipales
        padNum(0, 13) + // 214-228 impuestos internos
        'PES' + // 229-231 moneda
        '0001000000' + // 232-241 tipo de cambio (1,000000)
        String(cantAlicuotas) + // 242 cantidad alícuotas
        operationCode(r) + // 243 código operación
        padNum(0, 13) + // 244-258 otros tributos
        fecha // 259-266 fecha vencimiento pago
      );
    })
    .join('\r\n');
}

/** VENTAS_ALICUOTAS — 62 posiciones por alícuota. */
export function buildVentasAlicuotasTxt(rows: LibroIvaVentaRow[]): string {
  const lines: string[] = [];
  const sorted = [...rows].sort(
    (a, b) =>
      a.issuedAt.localeCompare(b.issuedAt) ||
      a.invoiceType - b.invoiceType ||
      a.pointOfSale - b.pointOfSale ||
      a.invoiceNumber - b.invoiceNumber
  );
  for (const r of sorted) {
    for (const a of resolveAlicuotas(r)) {
      lines.push(
        padZeros(r.invoiceType, 3) + // 1-3 tipo cbte
          padZeros(r.pointOfSale, 5) + // 4-8 punto de venta
          padZeros(r.invoiceNumber, 20) + // 9-28 nro cbte
          padNum(a.baseImp, 13) + // 29-43 neto gravado
          a.code + // 44-47 alícuota
          padNum(a.importe, 13) // 48-62 impuesto liquidado
      );
    }
  }
  return lines.join('\r\n');
}

export const LIBRO_IVA_CBTE_ROW_LENGTH = 266;
export const LIBRO_IVA_ALICUOTAS_ROW_LENGTH = 62;
